// Deck Builder — inspired by https://www.beybladexiligan.com/deck-builder.
// Three Bey panels, each with six part slots (Blade, Lockchip, Over Blade,
// Assist Blade, Ratchet, Bit). A "Repeating Parts" toggle relaxes the
// uniqueness check across beys. Parts library auto-refreshes from
// /api/components so newly added components are picked up immediately.

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { t } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { ArrowLeft, ArrowsClockwise, FloppyDisk, ArrowUUpLeft, ArrowUUpRight, Shuffle, DiceFive, Broom, Export, DownloadSimple, UploadSimple } from "@phosphor-icons/react";
import DeckStatsPanel from "../components/DeckStatsPanel";

const SLOT_META = [
  // The "blade" slot dynamically switches its options between the `blade`
  // catalogue and the `main_blade` catalogue depending on whether the same
  // bey has an over_blade selected. That switch happens inside BeyPanel.
  { key: "blade", labelKey: "blade", type: "blade", accent: "#FF0055" },
  { key: "lockchip", labelKey: "lockchip", type: "lockchip", accent: "#E2FF00" },
  { key: "over_blade", labelKey: "overblade", type: "overblade", accent: "#9B7AFF" },
  { key: "assist_blade", labelKey: "assistBlade", type: "assist_blade", accent: "#00E5FF" },
  { key: "ratchet", labelKey: "ratchet", type: "ratchet", accent: "#FF0055" },
  { key: "bit", labelKey: "bit", type: "bit", accent: "#00E5FF" },
];

const EMPTY_BEY = {
  blade: "", lockchip: "", over_blade: "", assist_blade: "", ratchet: "", bit: "",
  is_ux_infinity: false,
};

export default function DeckBuilder() {
  const { user } = useAuth();
  const { id } = useParams();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const beyCount = (() => {
    const q = parseInt(searchParams.get("beys"), 10);
    if (q === 6) return 6;
    if (q === 4) return 4;
    return 3;
  })();
  const isPokeblade = beyCount === 6;
  const isBeyPong = beyCount === 4;
  const [components, setComponents] = useState([]);
  const [form, setForm] = useState(() => ({
    name: "",
    archetype: "Balanced",
    description: "",
    beys: Array.from({ length: beyCount }, () => ({ ...EMPTY_BEY })),
    repeating_parts: false,
  }));
  const [busy, setBusy] = useState(false);
  const [refreshingParts, setRefreshingParts] = useState(false);
  // History stack for beys only (undo/redo). Each entry is a deep-cloned
  // snapshot of the previous `form.beys` value.
  const [pastBeys, setPastBeys] = useState([]);
  const [futureBeys, setFutureBeys] = useState([]);

  // Commit a new beys array while updating history (called by every user
  // action that mutates beys). Deep-clones snapshots so future edits don't
  // mutate stored history entries.
  const commitBeys = (mkNext) => {
    setForm((prev) => {
      const nextBeys = typeof mkNext === "function" ? mkNext(prev.beys) : mkNext;
      // Skip if nothing actually changed
      if (JSON.stringify(nextBeys) === JSON.stringify(prev.beys)) return prev;
      setPastBeys((p) => [...p.slice(-49), prev.beys.map((b) => ({ ...b }))]);
      setFutureBeys([]);
      return { ...prev, beys: nextBeys };
    });
  };

  const undo = () => {
    if (pastBeys.length === 0) return;
    const prev = pastBeys[pastBeys.length - 1];
    setPastBeys((p) => p.slice(0, -1));
    setForm((f) => {
      setFutureBeys((fut) => [f.beys.map((b) => ({ ...b })), ...fut.slice(0, 49)]);
      return { ...f, beys: prev };
    });
  };

  const redo = () => {
    if (futureBeys.length === 0) return;
    const next = futureBeys[0];
    setFutureBeys((fut) => fut.slice(1));
    setForm((f) => {
      setPastBeys((p) => [...p.slice(-49), f.beys.map((b) => ({ ...b }))]);
      return { ...f, beys: next };
    });
  };

  const reloadParts = async () => {
    setRefreshingParts(true);
    try {
      const r = await api.get("/components");
      setComponents(r.data);
    } finally {
      setRefreshingParts(false);
    }
  };

  useEffect(() => { reloadParts(); }, []);

  // Global keyboard shortcuts: Ctrl/Cmd+Z (undo), Ctrl/Cmd+Shift+Z (redo)
  useEffect(() => {
    const onKey = (e) => {
      const isMod = e.ctrlKey || e.metaKey;
      if (!isMod) return;
      // ignore when focused inside input/textarea/select to keep native behavior
      const tag = (document.activeElement?.tagName || "").toLowerCase();
      if (["input", "textarea", "select"].includes(tag)) return;
      if (e.key === "z" && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if ((e.key === "z" && e.shiftKey) || e.key === "y") {
        e.preventDefault();
        redo();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pastBeys, futureBeys]);

  // Load existing deck for editing
  useEffect(() => {
    if (!id) return;
    api.get(`/decks/${id}`).then((r) => {
      const d = r.data;
      const want = beyCount;
      const beys = Array.from({ length: want }, (_, i) => {
        if (d.beys && d.beys[i]) {
          const raw = d.beys[i];
          // Normalize null slot values from the API back to empty strings so
          // the controlled <select> elements don't emit "changing controlled
          // to uncontrolled" warnings.
          const norm = { ...EMPTY_BEY };
          for (const k of Object.keys(EMPTY_BEY)) {
            if (raw[k] === null || raw[k] === undefined) continue;
            norm[k] = raw[k];
          }
          return norm;
        }
        return {
          ...EMPTY_BEY,
          blade: d.blades?.[i] || "",
          ratchet: d.ratchets?.[i] || "",
          bit: d.bits?.[i] || "",
        };
      });
      setForm({
        name: d.name || "",
        archetype: d.archetype || "Balanced",
        description: d.description || "",
        beys,
        repeating_parts: d.repeating_parts ?? true,
      });
    }).catch(() => toast.error(t.failed));
  }, [id, beyCount]);

  const optionsByType = useMemo(() => {
    const out = {};
    for (const meta of SLOT_META) {
      out[meta.type] = components
        .filter((c) => c.type === meta.type)
        .sort((a, b) => a.name.localeCompare(b.name));
    }
    // Main-blade catalogue is not tied to a slot key but the "blade" slot
    // dynamically switches to it when the same bey has an over_blade set.
    out.main_blade = components
      .filter((c) => c.type === "main_blade")
      .sort((a, b) => a.name.localeCompare(b.name));
    return out;
  }, [components]);

  const componentById = useMemo(() => {
    const m = {};
    for (const c of components) m[c.id] = c;
    return m;
  }, [components]);

  // Detect repeating-part violations across beys
  const violations = useMemo(() => {
    if (form.repeating_parts) return {};
    const seen = {};
    const v = {};
    for (let i = 0; i < form.beys.length; i++) {
      const b = form.beys[i];
      for (const meta of SLOT_META) {
        const val = b[meta.key];
        if (!val) continue;
        const key = `${meta.key}::${val}`;
        if (seen[key] !== undefined) {
          v[`${i}-${meta.key}`] = true;
          v[`${seen[key]}-${meta.key}`] = true;
        } else {
          seen[key] = i;
        }
      }
    }
    return v;
  }, [form.beys, form.repeating_parts]);

  const setSlot = (beyIndex, slotKey, value) => {
    commitBeys((prevBeys) =>
      prevBeys.map((b, i) => {
        if (i !== beyIndex) return b;
        const nb = { ...b, [slotKey]: value };
        // ---- Coherence rules applied atomically so undo restores state in
        // a single step (no chained useEffect commits).
        if (slotKey === "blade" && value) {
          // Selecting a BX/UX blade → clear the CX-only stack.
          const c = componentById[value];
          if (c && (c.series === "BX" || c.series === "UX")) {
            nb.lockchip = "";
            nb.over_blade = "";
            nb.assist_blade = "";
          }
          // Iter 24: selecting a CX-series blade → clear the separate overblade
          // slot because the CX main blade already fills that role.
          // Iter 27: CX Infinity blades are the exception — they pair WITH a
          // separate overblade, so keep any existing overblade in place.
          if (c && c.series === "CX" && !c.is_cx_infinity) nb.over_blade = "";
        }
        // Iter 24: when the user just picked a lockchip and the current blade
        // isn't CX-series, clear the blade so the filtered CX-only pool is
        // the only option offered next.
        if (slotKey === "lockchip" && value) {
          const c = componentById[nb.blade];
          if (c && c.series !== "CX") nb.blade = "";
        }
        if (slotKey === "over_blade") {
          // Blade slot switches between `blade` and `main_blade` catalogues
          // based on whether over_blade is populated. If the current blade
          // now belongs to the wrong catalogue, clear it in the same commit.
          const c = componentById[nb.blade];
          if (!value && c && c.type === "main_blade") nb.blade = "";
          if (value && c && c.type === "blade") nb.blade = "";
        }
        return nb;
      }),
    );
  };

  const clearBey = (beyIndex) => {
    commitBeys((prevBeys) =>
      prevBeys.map((b, i) => (i === beyIndex ? { ...EMPTY_BEY } : b)),
    );
  };

  const setUxInfinity = (beyIndex, value) => {
    commitBeys((prevBeys) =>
      prevBeys.map((b, i) => {
        if (i !== beyIndex) return b;
        const nb = { ...b, is_ux_infinity: value };
        if (value) {
          // Clear all CX-only slots + ratchet in ONE commit so undo is atomic.
          nb.ratchet = "";
          nb.lockchip = "";
          nb.over_blade = "";
          nb.assist_blade = "";
          // If the blade slot held a main_blade id (from a previous CX
          // configuration), clear it — it can never be valid in a UX Infinity
          // bey which is a one-piece BX/UX blade.
          const c = componentById[nb.blade];
          if (c && c.type === "main_blade") nb.blade = "";
        }
        return nb;
      }),
    );
  };

  const clearAllBeys = () => {
    commitBeys(() => Array.from({ length: beyCount }, () => ({ ...EMPTY_BEY })));
  };

  // Randomize a single bey (index). Picks BX or CX mode 50/50, then
  // random parts for each slot. Respects the `repeating_parts` toggle by
  // avoiding parts already selected in *other* beys.
  const buildRandomBey = (prevBeys, index, repeatingParts) => {
    const opts = optionsByType; // {blade: [...], ratchet: [...], ...}
    const usedByType = {};
    // Resolve which catalogue a bey's blade slot belongs to. When the bey has
    // an over_blade selected, blade is a main_blade id; otherwise it's a
    // regular blade id. Uniqueness tracking must respect that split.
    const resolvedTypeFor = (b, key) => {
      if (key === "blade") return b.over_blade ? "main_blade" : "blade";
      return SLOT_META.find((m) => m.key === key)?.type;
    };
    if (!repeatingParts) {
      for (let i = 0; i < prevBeys.length; i++) {
        if (i === index) continue;
        for (const meta of SLOT_META) {
          const v = prevBeys[i][meta.key];
          if (!v) continue;
          const t = resolvedTypeFor(prevBeys[i], meta.key);
          if (!t) continue;
          usedByType[t] = usedByType[t] || new Set();
          usedByType[t].add(v);
        }
      }
    }
    const pick = (type) => {
      const pool = (opts[type] || []).filter(
        (o) => !usedByType[type] || !usedByType[type].has(o.id),
      );
      if (pool.length === 0) return "";
      const c = pool[Math.floor(Math.random() * pool.length)];
      if (!repeatingParts) {
        usedByType[type] = usedByType[type] || new Set();
        usedByType[type].add(c.id);
      }
      return c.id;
    };
    const mode = Math.random() < 0.5 ? "bxux" : "cx";
    const bey = { ...EMPTY_BEY };
    // 20% chance of tagging the random bey as UX Infinity when we're picking
    // a BX/UX-shaped one (a CX stack doesn't need integrated ratchets).
    const isUxInfinity = mode === "bxux" && Math.random() < 0.2;
    bey.is_ux_infinity = isUxInfinity;
    if (mode === "bxux") {
      bey.blade = pick("blade");
    } else {
      bey.lockchip = pick("lockchip");
      bey.over_blade = pick("overblade");
      bey.assist_blade = pick("assist_blade");
      // For a CX bey the "blade" slot represents the main blade underneath
      // the over blade. Fill it from the main_blade catalogue if available.
      bey.blade = pick("main_blade");
    }
    if (!isUxInfinity) bey.ratchet = pick("ratchet");
    bey.bit = pick("bit");
    return bey;
  };

  const randomizeBey = (index) => {
    // Read repeating_parts from the current form value inside the state
    // update so a toggle-then-random applied in the same tick is honoured.
    setForm((prev) => {
      const nextBeys = prev.beys.map((b, i) =>
        i === index ? buildRandomBey(prev.beys, index, prev.repeating_parts) : b,
      );
      if (JSON.stringify(nextBeys) === JSON.stringify(prev.beys)) return prev;
      setPastBeys((p) => [...p.slice(-49), prev.beys.map((b) => ({ ...b }))]);
      setFutureBeys([]);
      return { ...prev, beys: nextBeys };
    });
  };

  const randomizeDeck = () => {
    setForm((prev) => {
      const next = prev.beys.map(() => ({ ...EMPTY_BEY }));
      for (let i = 0; i < next.length; i++) {
        next[i] = buildRandomBey(next, i, prev.repeating_parts);
      }
      if (JSON.stringify(next) === JSON.stringify(prev.beys)) return prev;
      setPastBeys((p) => [...p.slice(-49), prev.beys.map((b) => ({ ...b }))]);
      setFutureBeys([]);
      return { ...prev, beys: next };
    });
  };

  // ---- Deck sharing: JSON export / import -------------------------------
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState("");

  const exportDeck = () => {
    // Snapshot the deck as a portable JSON blob keyed by component NAME
    // rather than uuid so anyone can re-import even if their DB ids differ.
    const beysPayload = form.beys.map((b) => {
      const cell = (id) => {
        const c = componentById[id];
        return c ? { type: c.type, name: c.name } : null;
      };
      return {
        blade: cell(b.blade),
        lockchip: cell(b.lockchip),
        over_blade: cell(b.over_blade),
        assist_blade: cell(b.assist_blade),
        ratchet: cell(b.ratchet),
        bit: cell(b.bit),
        is_ux_infinity: !!b.is_ux_infinity,
      };
    });
    const payload = {
      __schema: "beyx.deck.v1",
      exported_at: new Date().toISOString(),
      name: form.name || "Untitled deck",
      archetype: form.archetype,
      description: form.description,
      repeating_parts: !!form.repeating_parts,
      beys: beysPayload,
    };
    const json = JSON.stringify(payload, null, 2);
    // Download as file
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(form.name || "deck").replace(/[^a-z0-9-_]+/gi, "-").toLowerCase()}.beyx.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    // Also copy to clipboard for one-paste Discord sharing
    if (navigator.clipboard) navigator.clipboard.writeText(json).catch(() => {});
    toast.success(t.exportSuccess);
  };

  const applyImport = () => {
    let data;
    try {
      data = JSON.parse(importText);
    } catch (_) {
      toast.error(t.importErrorParse);
      return;
    }
    if (!data || data.__schema !== "beyx.deck.v1" || !Array.isArray(data.beys)) {
      toast.error(t.importErrorSchema);
      return;
    }
    // Build name→id lookups per catalogue for resolution
    const byNameType = {};
    for (const c of components) {
      const k = `${c.type}::${c.name.toLowerCase()}`;
      byNameType[k] = c.id;
    }
    const resolveCell = (cell) => {
      if (!cell || !cell.name || !cell.type) return "";
      return byNameType[`${cell.type}::${String(cell.name).toLowerCase()}`] || "";
    };
    const beysCount = Math.min(form.beys.length, data.beys.length);
    const nextBeys = form.beys.map((b, i) => {
      if (i >= beysCount) return b;
      const src = data.beys[i] || {};
      return {
        blade: resolveCell(src.blade),
        lockchip: resolveCell(src.lockchip),
        over_blade: resolveCell(src.over_blade),
        assist_blade: resolveCell(src.assist_blade),
        ratchet: resolveCell(src.ratchet),
        bit: resolveCell(src.bit),
        is_ux_infinity: !!src.is_ux_infinity,
      };
    });
    commitBeys(() => nextBeys);
    setForm((prev) => ({
      ...prev,
      name: typeof data.name === "string" ? data.name : prev.name,
      archetype: typeof data.archetype === "string" ? data.archetype : prev.archetype,
      description: typeof data.description === "string" ? data.description : prev.description,
      repeating_parts: !!data.repeating_parts,
    }));
    setImportOpen(false);
    setImportText("");
    toast.success(t.importSuccess);
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error(t.deckNameRequired);
      return;
    }
    if (Object.keys(violations).length > 0) {
      toast.error(t.repeatingPartsViolation);
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        archetype: form.archetype,
        description: form.description,
        beys: form.beys.map((b) => ({
          blade: b.blade || null,
          lockchip: b.lockchip || null,
          over_blade: b.over_blade || null,
          assist_blade: b.assist_blade || null,
          ratchet: b.ratchet || null,
          bit: b.bit || null,
          is_ux_infinity: !!b.is_ux_infinity,
        })),
        repeating_parts: form.repeating_parts,
      };
      // Admins save to the public deck library (/admin/decks);
      // players save to their personal library (/me/decks).
      const adminMode = user?.role === "admin";
      const basePath = adminMode ? "/admin/decks" : "/me/decks";
      const url = id ? `${basePath}/${id}` : basePath;
      const method = id ? api.put : api.post;
      const r = await method(url, payload);
      toast.success(t.saved);
      nav(`/decks/${r.data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t.failed);
    } finally {
      setBusy(false);
    }
  };

  if (!user) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-20 text-center">
        <p className="text-white/60 mb-4">{t.deckBuilderLoginGate}</p>
        <Link
          to="/login"
          className="inline-block px-6 py-3 bg-[#FF0055] text-white text-xs uppercase tracking-[0.25em] font-bold"
        >
          {t.navLogin}
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
      <Link
        to="/me"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-bold text-white/60 hover:text-[#00E5FF] mb-6"
      >
        <ArrowLeft size={12} /> {t.myProfileTitle}
      </Link>

      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#FF0055] font-mono">
            // {t.arsenal}{isPokeblade ? " · POKEBLADE" : isBeyPong ? " · BEY-PONG" : ""}
          </div>
          <h1 className="font-display font-black uppercase italic text-5xl sm:text-6xl mt-2">
            {t.deckBuilderTitle}
          </h1>
          <p className="text-sm text-white/50 max-w-xl mt-3">
            {isPokeblade ? t.deckBuilderIntroPokeblade : isBeyPong ? t.beyPongDeckIntro : t.deckBuilderIntro}
          </p>
        </div>
        <button
          onClick={reloadParts}
          disabled={refreshingParts}
          data-testid="deck-builder-refresh-parts"
          className="inline-flex items-center gap-2 px-4 py-2 border border-white/15 text-white/70 hover:text-white hover:border-white/30 text-[10px] uppercase tracking-[0.25em] font-bold disabled:opacity-50"
        >
          <ArrowsClockwise size={12} weight="bold" className={refreshingParts ? "animate-spin" : ""} />
          {t.refreshParts}
        </button>
      </div>

      {/* Toolbar: history + random + clear */}
      <div
        data-testid="deck-toolbar"
        className="flex items-center gap-2 flex-wrap mb-6"
      >
        <ToolbarBtn
          onClick={undo}
          disabled={pastBeys.length === 0}
          testid="deck-undo"
          icon={<ArrowUUpLeft size={12} weight="bold" />}
          label={t.undo}
          hint="Ctrl/Cmd+Z"
        />
        <ToolbarBtn
          onClick={redo}
          disabled={futureBeys.length === 0}
          testid="deck-redo"
          icon={<ArrowUUpRight size={12} weight="bold" />}
          label={t.redo}
          hint="Ctrl/Cmd+Shift+Z"
        />
        <div className="w-px h-6 bg-white/10 mx-1" />
        <ToolbarBtn
          onClick={randomizeDeck}
          testid="deck-random-all"
          icon={<Shuffle size={12} weight="bold" />}
          label={t.randomDeck}
          accent="#E2FF00"
        />
        <ToolbarBtn
          onClick={clearAllBeys}
          testid="deck-clear-all"
          icon={<Broom size={12} weight="bold" />}
          label={t.clearAll}
        />
        <div className="w-px h-6 bg-white/10 mx-1" />
        <ToolbarBtn
          onClick={exportDeck}
          testid="deck-export"
          icon={<DownloadSimple size={12} weight="bold" />}
          label={t.exportJson}
          accent="#00E5FF"
          hint={t.exportHint}
        />
        <ToolbarBtn
          onClick={() => setImportOpen(true)}
          testid="deck-import"
          icon={<UploadSimple size={12} weight="bold" />}
          label={t.importJson}
          accent="#9B7AFF"
        />
      </div>

      {/* Deck meta */}
      <div className="grid lg:grid-cols-[1fr_auto_auto] gap-4 items-end mb-8 border-b border-white/10 pb-6">
        <div>
          <label className="text-[10px] uppercase tracking-[0.3em] font-bold text-white/40 block mb-2">
            {t.deckName}
          </label>
          <input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder={t.deckNamePlaceholder}
            data-testid="deck-name"
            className="w-full bg-white/5 backdrop-blur-md border border-white/15 rounded-lg px-3 py-2 text-base font-display font-bold uppercase italic focus:outline-none focus:border-[#FF0055] focus:bg-white/10 transition-colors"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.3em] font-bold text-white/40 block mb-2">
            {t.archetype}
          </label>
          <select
            value={form.archetype}
            onChange={(e) => setForm({ ...form, archetype: e.target.value })}
            data-testid="deck-archetype"
            className="bg-white/5 backdrop-blur-md border border-white/15 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#00E5FF] min-w-[140px]"
          >
            <option value="Attack" className="bg-[#0C0C14]">{t.archAttack}</option>
            <option value="Defense" className="bg-[#0C0C14]">{t.archDefense}</option>
            <option value="Stamina" className="bg-[#0C0C14]">{t.archStamina}</option>
            <option value="Balanced" className="bg-[#0C0C14]">{t.archBalanced}</option>
          </select>
        </div>
        <label
          className="flex items-center gap-2 cursor-pointer select-none border border-white/15 rounded-lg bg-white/5 backdrop-blur-md px-3 py-2"
          title={t.repeatingPartsHint}
        >
          <input
            type="checkbox"
            checked={form.repeating_parts}
            onChange={(e) => setForm({ ...form, repeating_parts: e.target.checked })}
            data-testid="deck-repeating-parts"
            className="accent-[#FF0055]"
          />
          <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/70">
            {t.repeatingParts}
          </span>
        </label>
      </div>

      {/* Two-column workspace: beys (left) + stats panel (right, sticky on lg+) */}
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        {/* Bey panels */}
        <div
          data-testid="bey-grid"
          data-bey-count={beyCount}
          className={`grid gap-5 ${
            beyCount === 6
              ? "sm:grid-cols-2 xl:grid-cols-3"
              : "sm:grid-cols-2 xl:grid-cols-3"
          }`}
        >
          {form.beys.map((bey, i) => (
            <BeyPanel
              key={i}
              index={i}
              bey={bey}
              onChange={(slot, v) => setSlot(i, slot, v)}
              onClear={() => clearBey(i)}
              onRandom={() => randomizeBey(i)}
              onToggleUxInfinity={(v) => setUxInfinity(i, v)}
              optionsByType={optionsByType}
              componentById={componentById}
              violations={violations}
            />
          ))}
        </div>

        {/* Sticky stats panel */}
        <aside className="lg:sticky lg:top-6 lg:self-start">
          <DeckStatsPanel beys={form.beys} components={components} />
        </aside>
      </div>

      <div className="mt-6">
        <label className="text-[10px] uppercase tracking-[0.3em] font-bold text-white/40 block mb-2">
          {t.description}
        </label>
        <textarea
          rows={2}
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          data-testid="deck-description"
          placeholder={t.deckDescriptionPlaceholder}
          className="w-full bg-transparent border border-white/15 px-3 py-2 text-sm focus:outline-none focus:border-[#00E5FF]"
        />
      </div>

      <div className="mt-8 flex justify-end gap-3">
        <Link
          to="/me"
          className="px-4 py-3 border border-white/20 text-white/70 hover:text-white text-xs uppercase tracking-[0.25em] font-bold"
        >
          {t.cancel}
        </Link>
        <button
          onClick={save}
          disabled={busy}
          data-testid="deck-builder-save"
          className="inline-flex items-center gap-2 px-7 py-3 bg-[#FF0055] hover:bg-[#FF3377] text-white font-bold uppercase tracking-[0.25em] text-xs disabled:opacity-50 glow-pink"
        >
          <FloppyDisk size={14} weight="fill" /> {busy ? t.saving : t.save}
        </button>
      </div>

      {importOpen && (
        <div
          data-testid="deck-import-modal"
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          onClick={() => setImportOpen(false)}
        >
          <div
            className="bg-[#0C0C14] border border-white/15 max-w-2xl w-full p-6 rounded-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between pb-3 border-b border-white/10">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#9B7AFF] font-mono">
                  // {t.importJson}
                </div>
                <h3 className="font-display font-black uppercase italic text-2xl mt-1">
                  {t.importDeck}
                </h3>
              </div>
              <button
                onClick={() => setImportOpen(false)}
                className="text-white/60 hover:text-white text-2xl leading-none"
              >
                ×
              </button>
            </div>
            <p className="text-xs text-white/50 font-mono mt-4">{t.importHint}</p>
            <textarea
              rows={10}
              value={importText}
              onChange={(e) => setImportText(e.target.value)}
              placeholder='{"__schema": "beyx.deck.v1", "beys": [...]}'
              data-testid="deck-import-textarea"
              className="w-full mt-3 bg-transparent border border-white/15 px-3 py-2 text-xs font-mono focus:outline-none focus:border-[#9B7AFF] rounded-md"
            />
            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => setImportOpen(false)}
                className="px-4 py-2 text-xs uppercase tracking-[0.25em] font-bold text-white/60"
              >
                {t.cancel}
              </button>
              <button
                onClick={applyImport}
                data-testid="deck-import-apply"
                className="px-5 py-2 bg-[#9B7AFF]/20 border border-[#9B7AFF]/40 text-[#9B7AFF] hover:bg-[#9B7AFF]/30 font-bold uppercase tracking-[0.25em] text-xs"
              >
                {t.applyImport}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function BeyPanel({ index, bey, onChange, onClear, onRandom, onToggleUxInfinity, optionsByType, componentById, violations }) {
  const isUxInfinity = !!bey.is_ux_infinity;
  const hasOverblade = !!bey.over_blade;

  // Series-based auto-lock: if the selected blade is BX/UX, the whole CX
  // stack (lockchip, over_blade, assist_blade) is locked because a one-piece
  // BX/UX blade can't be part of a CX configuration.
  const bladeComp = bey.blade ? componentById[bey.blade] : null;
  const bladeSeriesLocks = !!(
    bladeComp && (bladeComp.series === "BX" || bladeComp.series === "UX")
  );
  // Iter 24: when the selected blade is CX-series, the CX stack already has
  // its main slot filled — lock the standalone overblade slot.
  // Iter 27: EXCEPT when the blade is a "CX Infinity" part — those are
  // designed to be used WITH a separate overblade, so leave the slot open.
  const bladeIsCx = !!(bladeComp && bladeComp.series === "CX" && !bladeComp.is_cx_infinity);
  const hasLockchip = !!bey.lockchip;
  const CX_STACK_SLOTS = new Set(["lockchip", "over_blade", "assist_blade"]);
  const UX_LOCKED_SLOTS = new Set(["ratchet", "lockchip", "over_blade", "assist_blade"]);

  const isLocked = (meta) => {
    if (isUxInfinity && UX_LOCKED_SLOTS.has(meta.key)) return true;
    if (bladeSeriesLocks && CX_STACK_SLOTS.has(meta.key)) return true;
    if (bladeIsCx && meta.key === "over_blade") return true;
    return false;
  };

  const lockedReason = (meta) => {
    if (isUxInfinity && UX_LOCKED_SLOTS.has(meta.key)) return t.lockedByUxInfinity;
    if (bladeSeriesLocks && CX_STACK_SLOTS.has(meta.key)) return t.lockedByBxUxSeries;
    if (bladeIsCx && meta.key === "over_blade") return t.lockedByCxBlade;
    return "";
  };

  // The "blade" slot picks from either the `blade` catalogue or the
  // `main_blade` catalogue based on whether the same bey has an over_blade
  // selected (CX stack uses a main blade underneath the over blade). When
  // the bey is flagged UX Infinity, the pool is further narrowed to blades
  // whose admin-set `has_integrated_ratchet` flag is true.
  const activeBladeCatalogue = hasOverblade ? "main_blade" : "blade";
  const optionsForSlot = (meta) => {
    if (meta.key === "blade") {
      let pool = optionsByType[activeBladeCatalogue] || [];
      if (isUxInfinity) pool = pool.filter((c) => c.has_integrated_ratchet);
      // Iter 24: when the CX stack is being assembled (lockchip present),
      // the blade dropdown must only offer CX-series main-blade parts.
      if (hasLockchip) pool = pool.filter((c) => c.series === "CX");
      return pool;
    }
    return optionsByType[meta.type] || [];
  };

  return (
    <div
      data-testid={`bey-panel-${index}`}
      className="relative rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-md p-5 hover:border-white/25 hover:bg-white/[0.06] transition-all duration-300 shadow-[0_4px_30px_rgba(0,0,0,0.4)]"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#FF0055] font-mono">
            // {t.bey} {index + 1}
          </div>
          {isUxInfinity && (
            <span
              data-testid={`bey-ux-badge-${index}`}
              className="text-[9px] uppercase tracking-[0.25em] font-bold px-2 py-0.5 border text-[#9B7AFF] border-[#9B7AFF]/40 bg-[#9B7AFF]/10"
            >
              {t.uxInfinity}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={onRandom}
            data-testid={`random-bey-${index}`}
            title={t.randomBey}
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.25em] font-bold text-[#E2FF00]/80 hover:text-[#E2FF00]"
          >
            <DiceFive size={12} weight="bold" /> {t.randomBey}
          </button>
          <button
            onClick={onClear}
            data-testid={`clear-bey-${index}`}
            className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/40 hover:text-[#FF0055]"
          >
            {t.clear}
          </button>
        </div>
      </div>

      <label
        data-testid={`bey-ux-toggle-row-${index}`}
        className="flex items-center gap-2 mb-4 cursor-pointer select-none"
        title={t.uxInfinityHint}
      >
        <input
          type="checkbox"
          checked={isUxInfinity}
          onChange={(e) => onToggleUxInfinity(e.target.checked)}
          data-testid={`bey-ux-toggle-${index}`}
          className="accent-[#9B7AFF]"
        />
        <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/70">
          {t.uxInfinity}
        </span>
      </label>
      <div className="space-y-3">
        {SLOT_META.map((meta) => (
          <SlotPicker
            key={meta.key}
            label={t[meta.labelKey]}
            options={optionsForSlot(meta)}
            value={bey[meta.key]}
            onChange={(v) => onChange(meta.key, v)}
            accent={meta.accent}
            error={violations[`${index}-${meta.key}`]}
            testid={`slot-${index}-${meta.key}`}
            placeholder={`${t.select} ${t[meta.labelKey]}`}
            locked={isLocked(meta)}
            lockedReason={lockedReason(meta)}
          />
        ))}
      </div>
    </div>
  );
}

function SlotPicker({ label, options, value, onChange, accent, error, testid, placeholder, locked, lockedReason }) {
  const selected = options.find((o) => o.id === value);
  const showStats =
    selected && typeof selected.attack === "number" && !locked;
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span
          className="text-[10px] uppercase tracking-[0.25em] font-bold font-mono"
          style={{ color: locked ? "rgba(255,255,255,0.25)" : accent }}
        >
          {label}
        </span>
        {locked ? (
          <span
            data-testid={`${testid}-locked`}
            title={lockedReason}
            className="text-[9px] uppercase tracking-[0.2em] font-bold text-white/40"
          >
            🔒 {t.locked}
          </span>
        ) : error ? (
          <span className="text-[9px] uppercase tracking-[0.2em] font-bold text-[#FF0055]">
            {t.duplicate}
          </span>
        ) : null}
      </div>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={locked}
        data-testid={testid}
        title={locked ? lockedReason : undefined}
        className={`w-full bg-white/5 backdrop-blur-sm border rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:bg-white/10 transition-colors ${
          locked ? "opacity-40 cursor-not-allowed" : ""
        }`}
        style={{
          borderColor: error ? "#FF0055" : "rgba(255,255,255,0.15)",
        }}
      >
        <option value="" className="bg-[#0C0C14]">{placeholder}</option>
        {options.length === 0 && (
          <option value="" disabled className="bg-[#0C0C14]">{t.noPartsYet}</option>
        )}
        {options.map((opt) => (
          <option key={opt.id} value={opt.id} className="bg-[#0C0C14]">
            {opt.name}
          </option>
        ))}
      </select>
      {showStats && (
        <div
          data-testid={`${testid}-stats`}
          className="mt-1.5 flex gap-1 flex-wrap"
        >
          <StatChip label="A" value={selected.attack} color="#FF0055" />
          <StatChip label="D" value={selected.defense} color="#00E5FF" />
          <StatChip label="S" value={selected.stamina} color="#E2FF00" />
          <StatChip label="B" value={selected.burst_resistance} color="#FF9B00" />
          {selected.is_cx_infinity && (
            <span
              data-testid={`${testid}-cx-infinity-badge`}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono font-bold border border-[#E2FF00]/50 bg-[#E2FF00]/10 text-[#E2FF00]"
            >
              {t.cxInfinityBadge}
            </span>
          )}
          {selected.has_integrated_ratchet && (
            <span
              data-testid={`${testid}-ux-infinity-badge`}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono font-bold border border-[#9B7AFF]/50 bg-[#9B7AFF]/10 text-[#9B7AFF]"
            >
              {t.uxInfinityBadge}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function StatChip({ label, value, color }) {
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono font-bold border"
      style={{ color, borderColor: `${color}40`, background: `${color}10` }}
    >
      <span className="opacity-70">{label}</span>
      <span>{value}</span>
    </span>
  );
}

function ToolbarBtn({ onClick, disabled, icon, label, testid, accent, hint }) {
  const c = accent || "#FFFFFF";
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      title={hint}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-white/15 bg-white/5 backdrop-blur-sm hover:bg-white/10 hover:border-white/30 text-[10px] uppercase tracking-[0.25em] font-bold disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
      style={{ color: accent || "rgba(255,255,255,0.8)" }}
    >
      <span style={{ color: c }}>{icon}</span>
      <span>{label}</span>
    </button>
  );
}
